import { Link } from "gatsby";
import { StateContext } from "../../lib/store";
import { useContext } from "react";

/**
 * Footer
 * @param logo
 * @param contactInfo
 * @param socialLinks
 * @param mainLinks
 * @param miscLinks
 * @param ctas
 * @param copyright
 * @returns {JSX.Element}
 * @constructor
 */
function Footer({
  logo,
  contactInfo,
  contactInfoLinks,
  socialLinks,
  mainLinks,
  miscLinks,
  ctas,
  copyright,
}) {
  const [state, dispatch] = useContext(StateContext);
  // No hamburger menu context don't render any dom
  if (!(state && state.footerMenu)) {
    return <></>;
  }
  return (
    <>
      <footer className="bg-blue-700 py-16 text-blue-100">
        <div className="max-w-6xl mx-auto px-4 text-sm">
          <div className="md:flex md:flex-wrap md:-mx-2">
            <div className="w-full md:w-1/2 lg:w-3/12 md:px-2 mb-6">
              <img
                alt={logo?.alt}
                className="w-40 mr-2 mb-4"
                src={logo?.image}
              />
              <p className="font-bold text-white">{contactInfo?.title}</p>
              <p>{contactInfo?.street}</p>
              <p>
                {contactInfo?.city}, {contactInfo?.state} {contactInfo?.zip}
              </p>
              <p className="mb-4">{contactInfo?.phone}</p>
              <p className="mb-2 md:mb-0">
                <a href={`mailto:${contactInfo?.email}`} className="underline">
                  {contactInfo?.email}
                </a>
              </p>
              <div className="py-2 flex justify-start items-center text-blue-100">
                {socialLinks &&
                  socialLinks.map(({ type, url }, i) => {
                    let iconPath = "";
                    let vb = "0 0 20 20";
                    switch (type) {
                      case "Instagram":
                        vb = "0 0 18 18";
                        iconPath =
                          "M17.946 5.29a6.606 6.606 0 0 0-.418-2.185 4.412 4.412 0 0 0-1.039-1.594A4.412 4.412 0 0 0 14.895.472a6.606 6.606 0 0 0-2.184-.418C11.75.01 11.444 0 9 0S6.25.01 5.29.054a6.606 6.606 0 0 0-2.185.418A4.412 4.412 0 0 0 1.51 1.511 4.412 4.412 0 0 0 .472 3.105a6.606 6.606 0 0 0-.418 2.184C.01 6.25 0 6.556 0 9s.01 2.75.054 3.71a6.606 6.606 0 0 0 .418 2.185 4.412 4.412 0 0 0 1.039 1.594 4.411 4.411 0 0 0 1.594 1.039 6.606 6.606 0 0 0 2.184.418C6.25 17.99 6.556 18 9 18s2.75-.01 3.71-.054a6.606 6.606 0 0 0 2.185-.418 4.602 4.602 0 0 0 2.633-2.633 6.606 6.606 0 0 0 .418-2.184C17.99 11.75 18 11.444 18 9s-.01-2.75-.054-3.71zm-1.62 7.347a4.978 4.978 0 0 1-.31 1.67 2.98 2.98 0 0 1-1.708 1.709 4.979 4.979 0 0 1-1.671.31c-.95.043-1.234.052-3.637.052s-2.688-.009-3.637-.052a4.979 4.979 0 0 1-1.67-.31 2.788 2.788 0 0 1-1.036-.673 2.788 2.788 0 0 1-.673-1.035 4.978 4.978 0 0 1-.31-1.671c-.043-.95-.052-1.234-.052-3.637s.009-2.688.052-3.637a4.979 4.979 0 0 1 .31-1.67 2.788 2.788 0 0 1 .673-1.036 2.788 2.788 0 0 1 1.035-.673 4.979 4.979 0 0 1 1.671-.31c.95-.043 1.234-.052 3.637-.052s2.688.009 3.637.052a4.979 4.979 0 0 1 1.67.31 2.788 2.788 0 0 1 1.036.673 2.788 2.788 0 0 1 .673 1.035 4.979 4.979 0 0 1 .31 1.671c.043.95.052 1.234.052 3.637s-.009 2.688-.052 3.637zM9 4.378A4.622 4.622 0 1 0 13.622 9 4.622 4.622 0 0 0 9 4.378zM9 12a3 3 0 1 1 3-3 3 3 0 0 1-3 3zm5.884-7.804a1.08 1.08 0 1 1-1.08-1.08 1.08 1.08 0 0 1 1.08 1.08z";
                        break;
                      case "YouTube":
                        iconPath =
                          "M10 2.3C.172 2.3 0 3.174 0 10s.172 7.7 10 7.7 10-.874 10-7.7-.172-7.7-10-7.7zm3.205 8.034l-4.49 2.096c-.393.182-.715-.022-.715-.456V8.026c0-.433.322-.638.715-.456l4.49 2.096c.393.184.393.484 0 .668z";
                        break;
                      case "Facebook":
                        iconPath =
                          "M17 1H3c-1.1 0-2 .9-2 2v14c0 1.101.9 2 2 2h7v-7H8V9.525h2v-2.05c0-2.164 1.212-3.684 3.766-3.684l1.803.002v2.605h-1.197c-.994 0-1.372.746-1.372 1.438v1.69h2.568L15 12h-2v7h4c1.1 0 2-.899 2-2V3c0-1.1-.9-2-2-2z";
                        break;
                      case "TikTok":
                        vb = "0 0 448 512";
                        iconPath =
                          "M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z";
                        break;
                      case "LinkedIn":
                        iconPath =
                          "M5 3c0 1.1-.7 2-2 2-1.2 0-2-.9-2-1.9C1 2 1.8 1 3 1s2 .9 2 2zM1 19h4V6H1v13zM14.6 6.2c-2.1 0-3.3 1.2-3.8 2h-.1l-.2-1.7H6.9c0 1.1.1 2.4.1 3.9V19h4v-7.1c0-.4 0-.7.1-1 .3-.7.8-1.6 1.9-1.6 1.4 0 2 1.2 2 2.8V19h4v-7.4c0-3.7-1.9-5.4-4.4-5.4z";
                        break;
                      case "Twitter":
                        iconPath =
                          "M17.316 6.246c.008.162.011.326.011.488 0 4.99-3.797 10.742-10.74 10.742-2.133 0-4.116-.625-5.787-1.697a7.577 7.577 0 0 0 5.588-1.562 3.779 3.779 0 0 1-3.526-2.621 3.858 3.858 0 0 0 1.705-.065 3.779 3.779 0 0 1-3.028-3.703v-.047a3.766 3.766 0 0 0 1.71.473 3.775 3.775 0 0 1-1.168-5.041 10.716 10.716 0 0 0 7.781 3.945 3.813 3.813 0 0 1-.097-.861 3.773 3.773 0 0 1 3.774-3.773 3.77 3.77 0 0 1 2.756 1.191 7.602 7.602 0 0 0 2.397-.916 3.789 3.789 0 0 1-1.66 2.088 7.55 7.55 0 0 0 2.168-.594 7.623 7.623 0 0 1-1.884 1.953z";
                        break;
                      default:
                        throw Error(
                          `Please use a valid socialLink type since "${type}" doesn't exist!`
                        );
                    }

                    return (
                      <a
                        className="block flex items-center hover:text-white mr-3"
                        href={url}
                        key={i}
                      >
                        <svg
                          key={i}
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox={vb}
                          className="fill-current w-5 h-5"
                        >
                          <title>{type}</title>
                          <path d={iconPath} />
                        </svg>
                      </a>
                    );
                  })}
              </div>
              {Array.isArray(contactInfoLinks) &&
                contactInfoLinks.map(({ label, items }, i) => {
                  return (
                    <div className="mt-4" key={`div-${i}`}>
                      <ul className="mb-10">
                        {items.map(({ label, url }, i) => {
                          return (
                            <li key={i} className="pb-1 mb-2 md:mb-0">
                              <a
                                href={url}
                                className="hover:underline hover:text-white"
                              >
                                {label}
                              </a>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  );
                })}
            </div>
            <div
              className={`w-full ${
                ctas && ctas.length === 0
                  ? "md:w-1/2 lg:w-6/12"
                  : "md:w-1/2 lg:w-3/12"
              } md:px-2 mb-6`}
            >
              <ul className="mb-10 text-lg">
                {mainLinks &&
                  mainLinks.map(({ mainLinksLabel, label, url }, i) => {
                    return (
                      <li key={i} className="pb-2">
                          <a
                            href={url}
                            className="hover:underline hover:text-white"
                          >
                            {label}
                          </a>
                      </li>
                    );
                  })}
              </ul>
            </div>
            <div className="w-full md:w-1/2 lg:w-3/12 md:px-2 mb-6">
              {miscLinks &&
                miscLinks.map(({ label, items }, i) => {
                  return (
                    <div key={i}>
                      <h3 className="text-white mb-5 md:mb-3 md:text-xs uppercase tracking-widest">
                        {label}
                      </h3>
                      <ul className="mb-10">
                        {items &&
                          items.map(({ label, url }, i) => {
                            return (
                              <li key={i} className="pb-1 mb-2 md:mb-0">
                                {url.startsWith("http") ? (
                                  <a
                                    href={url}
                                    className="hover:underline hover:text-white"
                                  >
                                    {label}
                                  </a>
                                ) : (
                                  <a
                                    href={url}
                                    className="hover:underline hover:text-white"
                                  >
                                    {label}
                                  </a>
                                )}
                              </li>
                            );
                          })}
                      </ul>
                    </div>
                  );
                })}
            </div>
            <div className="w-full md:w-1/2 lg:w-3/12 md:px-2 mr-auto mb-6">
              <ul className="text-center">
                {state.footerMenu.ctas.map(({ label, url }, i) => {
                  return (
                    <li key={i} className="pb-3">
                      <a
                        href={url}
                        className="rounded-lg block text-white tracking-tight font-bold bg-blue-500 shadow px-8 py-2 hover:text-yellow-500 hover:bg-blue-600"
                      >
                        {label}
                      </a>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
          <div className="text-xs border-t border-blue-600 py-2">
            <ul className="flex">
              <li>
                {state.footerMenu.copyright.year + " "}
                <a
                  href={state.footerMenu.copyright.website.url}
                  className="hover:text-white"
                >
                  {state.footerMenu.copyright.website.label}
                </a>
              </li>
              {state.footerMenu.copyright.links.map(
                ({ label, url, target }, i) => {
                  return (
                    <li key={i} className="px-4">
                      <a
                        href={url}
                        target={target || "_self"}
                        rel={target === "_blank" ? "noreferrer" : false}
                        className="hover:text-white"
                      >
                        {label}
                      </a>
                    </li>
                  );
                }
              )}
            </ul>
          </div>
        </div>
      </footer>
    </>
  );
}

export default Footer;
